/* eslint-disable react-hooks/rules-of-hooks */
import React, { useMemo, useState } from 'react'
import {
  useRecoilRefresher_UNSTABLE,
  useRecoilState,
  useRecoilValue,
  useResetRecoilState,
  useSetRecoilState,
} from 'recoil'
import styled from '@emotion/styled'
import { PaymentIntent, StripeElementsOptions } from '@stripe/stripe-js'
import { Elements } from '@stripe/react-stripe-js'
import type Stripe from 'stripe'
import { Typography } from '@mui/material'
import { Colors, Shapes } from 'src/components/theme'
import { PaymentsLoading } from 'src/views/subscriptions/loading'
import CheckoutForm from 'src/views/subscriptions/create/checkout-form'
import { paymentsClient } from 'src/clients'
import { browserLogger } from 'src/utils/browser-logger'
import getStripePromise from 'src/views/subscriptions/create/get-stripe'
import { formatVal } from 'src/utils/number-format'
import { FlexColumn } from 'src/components/flex-containers'
import { SubscriptionPlanButton } from 'src/views/subscriptions/plan-button'
import { SUBSCRIPTION_MODAL_CONTENT_DIMENSIONS } from 'src/constants/subscriptions'
import { SubscriptionModalHeader } from 'src/views/subscriptions/header'
import { SUBSCRIPTION_UPDATE_STEPS } from 'src/atoms/payments'

import * as paymentsAtoms from 'src/atoms/payments'
import * as userAtoms from 'src/atoms/user'

export const Wrapper = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: ${Shapes.spacing[4]}px;
  width: ${SUBSCRIPTION_MODAL_CONTENT_DIMENSIONS.width}px;
  max-width: ${SUBSCRIPTION_MODAL_CONTENT_DIMENSIONS.width}px;
  margin: 0 auto;
`
const PlansWrapper = styled.div`
  display: flex;
  gap: ${Shapes.spacing[4]}px;
`

const SelectedPlanForm = () => {
  const setActiveStep = useSetRecoilState(paymentsAtoms.subscriptionUpdateStep)
  const selectedPlan = useRecoilValue(paymentsAtoms.selectedPaymentPlan)
  const selectedPlanPrice = useRecoilValue(paymentsAtoms.selectedPaymentPlanPrice)
  const resetSelectedPlanPrice = useResetRecoilState(paymentsAtoms.selectedPaymentPlanPrice)
  const setPaymentIntent = useSetRecoilState(paymentsAtoms.paymentIntent)
  const clientSecret = useRecoilValue(paymentsAtoms.clientSecret)
  const currentUser = useRecoilValue(userAtoms.currentUser)
  const refreshUser = useRecoilRefresher_UNSTABLE(userAtoms.currentUser)
  const [error, setError] = useState<string | undefined>(undefined)

  const onFormBack = async () => {
    resetSelectedPlanPrice()
  }

  const stripePromise = useMemo(async () => {
    return await getStripePromise()
  }, [])

  const payTitle = useMemo(() => {
    if (!selectedPlanPrice || !selectedPlanPrice.unit_amount) {
      return undefined
    }
    return `Pay $${formatVal(selectedPlanPrice.unit_amount / 100, 'number')}`
  }, [selectedPlanPrice])

  const onSuccess = async (paymentIntent: PaymentIntent | undefined) => {
    if (!paymentIntent || !currentUser || !selectedPlan) {
      return
    }
    try {
      setPaymentIntent(paymentIntent)
      await paymentsClient.confirmPayment({
        paymentIntentId: paymentIntent.id,
        userId: currentUser.id,
        planId: selectedPlan.id,
      })
      // Refresh user to update the user's tier
      refreshUser()
      // Shallow routing to update the url
      setActiveStep(SUBSCRIPTION_UPDATE_STEPS.CONFIRMATION)
    } catch (e: any) {
      setError(e.message)
    }
  }

  if (typeof clientSecret === 'object') {
    return (
      <Typography variant="text1m" color={Colors.redDark}>
        Error: {clientSecret.error.message}
      </Typography>
    )
  }

  if (!stripePromise || !clientSecret) {
    browserLogger.error('stripePromise or clientSecret is not defined', { stripePromise, clientSecret })
    return <PaymentsLoading />
  }

  const options: StripeElementsOptions = {
    clientSecret,
    appearance: {
      theme: 'stripe',
      disableAnimations: true,
    },
  }

  return (
    <>
      <Elements options={options} stripe={stripePromise}>
        <CheckoutForm
          onBack={onFormBack}
          clientSecret={clientSecret}
          payTitle={payTitle}
          onSuccess={onSuccess}
          error={error}
        />
      </Elements>
    </>
  )
}

export const SelectedPlan = () => {
  const selectedPlan = useRecoilValue(paymentsAtoms.selectedPaymentPlan)
  const [selectedPlanPrice, setSelectedPlanPrice] = useRecoilState(paymentsAtoms.selectedPaymentPlanPrice)

  const onSelectPlanPrice = (price: Stripe.Price) => () => setSelectedPlanPrice(price)

  return (
    <Wrapper>
      <SubscriptionModalHeader>
        <Typography variant="header2" color={Colors.textBlack}>
          Professional Plans
        </Typography>
        <Typography variant="textmdm" color={Colors.textLight}>
          Fill out the form below to start your subscription
        </Typography>
      </SubscriptionModalHeader>
      <FlexColumn gap={Shapes.spacing[2]}>
        <Typography variant="textmd">Billing Plan</Typography>
        <PlansWrapper>
          {selectedPlan &&
            selectedPlan.prices.map((price) => (
              <SubscriptionPlanButton
                key={price.id}
                active={!!selectedPlanPrice && price.id === selectedPlanPrice.id}
                price={price}
                onClick={onSelectPlanPrice(price)}
              />
            ))}
        </PlansWrapper>
      </FlexColumn>
      <React.Suspense fallback={<PaymentsLoading />}>
        <SelectedPlanForm />
      </React.Suspense>
    </Wrapper>
  )
}
